import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { store } from '../store';
import { EndPoints } from '../../axios/endPoints';
import { axiosInstance } from '../../axios/axiosHandler';
import { SubscriptionDto } from '../../types/profile.types';

export interface SubscriptionItem {
  name: string;
  value: string;
}

export interface CurrentSubscriptionDetails {
  name: string;
  status: string;
  items: SubscriptionItem[];
}

export interface Auth {
  loading: boolean;
  user: {
    email: string | null;
    role: string | null;
  };
  token: string | null;
  currentSubscription: SubscriptionDto | null;
}

interface AuthProps {
  success: boolean;
  expiration: string | null;
  user: string | null;
  userRoles: [string] | null;
  token: string | null;
  currentSubscription?: SubscriptionDto | null;
  message: string | null;
}

export interface LogInData {
  email: string;
  password: string;
  remember: boolean;
}

export function authHeader() {
  const token = authToken();
  const isLoggedIn = !!token;
  if (isLoggedIn) {
    return {
      headers: { Authorization: `Bearer ${token}` },
    };
  } else {
    return {};
  }
}

function authToken() {
  return store.getState().auth?.token;
}

const defaultAuthState: Auth = {
  loading: false,
  user: {
    email: null,
    role: null,
  },
  token: null,
  currentSubscription: null,
};

function loadInitialAuthState(): Auth {
  const savedAuth = localStorage.getItem('auth');
  if (!savedAuth) {
    return defaultAuthState;
  }

  try {
    const parsed = JSON.parse(savedAuth);
    return { ...defaultAuthState, ...parsed };
  } catch {
    return defaultAuthState;
  }
}

export const performLogIn = createAsyncThunk<AuthProps, LogInData, { rejectValue: AuthProps }>(
  'LogIn',
  async (payload: LogInData, thunkAPI) => {
    try {
      const response = await axiosInstance.post(EndPoints.LOG_IN, {
        username: payload.email,
        password: payload.password,
        remember: payload.remember
      });
      
      return response.data as AuthProps;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return thunkAPI.rejectWithValue(error.response.data as AuthProps);
      }

      // Fallback for network errors
      return thunkAPI.rejectWithValue(
        error?.response?.data ?? {
          success: false,
          expiration: null,
          user: null,
          userRoles: null,
          token: null,
          currentSubscription: null,
          message: 'Something went wrong.'
        } as AuthProps
      );
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState: loadInitialAuthState(),
  reducers: {
    updateCurrentSubscription: (state, action: PayloadAction<SubscriptionDto | null>) => {
      const nextState: Auth = {
        ...state,
        currentSubscription: action.payload ?? null,
      };

      localStorage.setItem('auth', JSON.stringify(nextState));
      return nextState;
    },
    handleLogOut: (state) => {
      state = {
        ...defaultAuthState,
      };

      localStorage.setItem('auth', JSON.stringify(state));
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(performLogIn.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(performLogIn.rejected, () => {
      const nextState: Auth = {
        ...defaultAuthState,
      };

      return nextState;
    });

    builder.addCase(performLogIn.fulfilled, (state, action) => {
      const { success, user, userRoles, token, currentSubscription } =
        action.payload as AuthProps;

      if (success) {
        state = {
          loading: false,
          user: {
            email: user ?? null,
            role: userRoles?.[0] ?? null,
          },
          token: token ?? null,
          currentSubscription: currentSubscription ?? null,
        };

        localStorage.setItem('auth', JSON.stringify(state));

        return state;
      } else
        state = {
          ...defaultAuthState,
        };

      return state;
    });
  },
});

export const { handleLogOut, updateCurrentSubscription } = authSlice.actions;

export const authSelector = (state: RootState) => state.auth;
